import { useTheme } from '@mui/material';
import MenuItem from '@mui/material/MenuItem';
import MenuList from '@mui/material/MenuList';
import Paper from '@mui/material/Paper';
import Popper from '@mui/material/Popper';
import Typography from '@mui/material/Typography';

import { TreadAgentSkillBasic } from '~hooks/useTreadAgent/models';

export const SKILL_PICKER_LISTBOX_ID = 'tread-agent-skill-picker';

export function skillPickerOptionId(index: number) {
  return `${SKILL_PICKER_LISTBOX_ID}-option-${index}`;
}

// Caps the menu at under half the 600px compact panel so it never swallows the transcript
// behind it. Roughly four of the two-line rows below.
const MENU_MAX_HEIGHT_IN_PX = 260;

interface SkillPickerMenuProps {
  activeIndex: number;
  anchorEl: HTMLElement | null;
  isOpen: boolean;
  onHighlight: (index: number) => void;
  onSelect: (skillName: string) => void;
  skills: TreadAgentSkillBasic[];
}

export default function SkillPickerMenu({
  activeIndex,
  anchorEl,
  isOpen,
  onHighlight,
  onSelect,
  skills,
}: SkillPickerMenuProps) {
  const theme = useTheme();

  return (
    <Popper
      anchorEl={anchorEl}
      // Stays in the panel's stacking context so it can't paint over a dialog.
      disablePortal
      // anchorEl comes from a ref callback and is null on the first render.
      open={isOpen && Boolean(anchorEl)}
      placement="top-start"
      // Later siblings, like the starter prompts, paint over it without a z-index.
      sx={{ width: anchorEl?.offsetWidth, zIndex: theme.zIndex.modal }}
    >
      <Paper
        elevation={0}
        sx={{
          border: `1px solid ${theme.brandV2.colors.treadGray7}`,
          borderRadius: theme.brandV2.borderRadius,
          boxShadow: '0 4px 16px rgba(0, 0, 0, 0.08)',
          mb: 0.5,
          overflow: 'hidden',
        }}
      >
        <MenuList
          autoFocusItem={false}
          dense
          id={SKILL_PICKER_LISTBOX_ID}
          role="listbox"
          sx={{ maxHeight: `${MENU_MAX_HEIGHT_IN_PX}px`, overflowY: 'auto', py: 0.5 }}
        >
          {skills.map((skill, index) => (
            <MenuItem
              key={skill.name}
              aria-selected={index === activeIndex}
              data-test-id={`tread-agent-skill-option-${skill.name}`}
              id={skillPickerOptionId(index)}
              onClick={() => onSelect(skill.name)}
              // Keeps focus in the composer, which the highlight is driven from.
              onMouseDown={(event) => event.preventDefault()}
              onMouseEnter={() => onHighlight(index)}
              role="option"
              selected={index === activeIndex}
              sx={{
                alignItems: 'flex-start',
                display: 'flex',
                flexDirection: 'column',
                gap: 0.25,
                px: 1.25,
                py: 0.75,
                // MUI tints a selected MenuItem with primary.main — the brand yellow.
                '&.Mui-selected, &.Mui-selected:hover': { bgcolor: 'action.selected' },
              }}
            >
              <Typography sx={{ fontSize: '13px', fontWeight: 600 }}>
                /{skill.name}
              </Typography>

              <Typography
                sx={{
                  color: 'text.secondary',
                  display: '-webkit-box',
                  fontSize: '12px',
                  lineHeight: 1.4,
                  overflow: 'hidden',
                  whiteSpace: 'normal',
                  WebkitBoxOrient: 'vertical',
                  // Skill descriptions are routing copy written for the model and run to
                  // paragraphs; two lines is enough to tell them apart.
                  WebkitLineClamp: 2,
                }}
              >
                {skill.description}
              </Typography>
            </MenuItem>
          ))}
        </MenuList>
      </Paper>
    </Popper>
  );
}
